import { useState } from "react";

import { isEmpty } from "lodash";

import { translate } from "app/filters/i18nFilter";
import type { IAppsSdkDescriptionItemDto } from "app/pages/sales-intelligence/domain/sdk/types/dto";
import type {
    AppsSdksSignalSelection,
    IAppsSignalFilters,
} from "app/pages/sales-intelligence/domain/signals/types/apps-signal";
import { appsSdksSignalChangeTypeMap } from "app/pages/sales-intelligence/domain/signals/types/apps-signal";
import { signalTimeframePeriodMap } from "app/pages/sales-intelligence/domain/signals/types/timeframe";
import {
    getAppsSdksRenewalMonths,
    getAppsSdksSelection,
    getSdkSelectionValues,
    isAppsSdksSignalModel,
    mergeAppsSdksFilters,
} from "app/pages/sales-intelligence/domain/signals/utils/apps-signal";
import SignalRenewalPeriodFilter from "app/pages/sales-intelligence/modules/signals/components/SignalRenewalPeriodFilter/SignalRenewalPeriodFilter";
import SignalTimeframeSelect from "app/pages/sales-intelligence/sub-modules/signals/components/filters/SignalTimeframeSelect/SignalTimeframeSelect";
import { StyledModalFooter } from "app/pages/sales-intelligence/sub-modules/signals/components/modals/SignalsModal/styles";
import SignalFiltersViewHeader from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalFiltersViewHeader/SignalFiltersViewHeader";
import SignalFiltersWrapper from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalFiltersWrapper/SignalFiltersWrapper";
import { StyledDropdownLabelGapWrapped } from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalsDropdownLabel/styles";
import {
    StyledFiltersViewContent,
    StyledFiltersViewInner,
    StyledInlineFilterWrapper,
    StyledNotificationWrapper,
} from "app/pages/sales-intelligence/sub-modules/signals/components/styles";
import SignalSdksFilterTracked from "app/pages/sales-intelligence/sub-modules/signals/containers/SignalSdksFilter/SignalSdksFilterTracked";
import useSignalFiltersState from "app/pages/sales-intelligence/sub-modules/signals/hooks/useFiltersState";
import appsSignalTimeframeConfig from "app/pages/sales-intelligence/sub-modules/signals/models/apps/AppsSignalTimeframe";
import type { SignalTimeframe } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalTimeframe";
import type SignalTemplate from "app/pages/sales-intelligence/sub-modules/signals/models/templates/SignalTemplate";
import type SignalFiltersViewProps from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalFiltersViewProps";
import signalsI18nKeys from "app/pages/sales-intelligence/sub-modules/signals/utils/i18n";

export type AppsSignalFiltersViewProps = SignalFiltersViewProps<Partial<IAppsSignalFilters>> & {
    sdkDescriptions: readonly IAppsSdkDescriptionItemDto[];
    isSdkDescriptionsPending: boolean;
};

const AppsSignalFiltersView = (props: AppsSignalFiltersViewProps) => {
    const {
        isActive,
        signalAlias,
        sdkDescriptions,
        isSdkDescriptionsPending,
        filters,
        notificationView,
        renderSubmit,
        renderTemplateView,
    } = props;

    const { currentFilters, setCurrentFilters } = useSignalFiltersState(filters, props.onFiltersChange);
    const [selectedTimeframe, setSelectedTimeframe] = useState<SignalTimeframe | undefined>(() =>
        appsSignalTimeframeConfig.fromFilters(currentFilters),
    );

    const selectedValue = getAppsSdksSelection(currentFilters);
    const isValueEmpty = isEmpty(getSdkSelectionValues(selectedValue));

    /** Every write goes through the nested merge: a timeframe patch must not drop the SDK selection. */
    function handleChange(updatedFilters: Partial<IAppsSignalFilters>) {
        setCurrentFilters((prevFilters) => mergeAppsSdksFilters(prevFilters, updatedFilters));
    }

    function handleSdksChange(selection: AppsSdksSignalSelection) {
        handleChange({ appsSdks: selection });
    }

    function handleTimeframeSelect(timeframe: SignalTimeframe) {
        setSelectedTimeframe(timeframe);
        handleChange(appsSignalTimeframeConfig.toFilters(timeframe));
    }

    /** Templates carry an empty SDK selection, so the current one is re-applied over the template patch. */
    function handleTemplateSelect(template: Readonly<SignalTemplate<IAppsSignalFilters>>) {
        setCurrentFilters((prevFilters) =>
            mergeAppsSdksFilters(prevFilters, {
                ...template.filters,
                appsSdks: { ...template.filters.appsSdks, ...getAppsSdksSelection(prevFilters) },
            }),
        );
        setSelectedTimeframe(appsSignalTimeframeConfig.fromFilters(template.filters));
    }

    function handleSubmit() {
        props.onFiltersSubmit(mergeAppsSdksFilters(filters, currentFilters));
    }

    const isRenewal = currentFilters.appsSdks?.type === appsSdksSignalChangeTypeMap.renewalComingUp;
    const renewalMonths = getAppsSdksRenewalMonths(currentFilters.appsSdks);

    function handleRenewalMonthsChange(months: number) {
        handleChange({ appsSdks: { period: signalTimeframePeriodMap.month, periodAmount: months } });
    }

    const isSubmitDisabled =
        !isAppsSdksSignalModel(currentFilters.appsSdks) || (isRenewal && renewalMonths === undefined);

    const sdksFilter = (
        <StyledInlineFilterWrapper data-automation="apps-signal-sdks-filter">
            <SignalSdksFilterTracked
                filterKey="sdks"
                isValueEmpty={isValueEmpty}
                isDisabled={isSdkDescriptionsPending}
                signalAlias={signalAlias}
                selectedValue={selectedValue}
                options={sdkDescriptions}
                onValueChange={handleSdksChange}
            />
        </StyledInlineFilterWrapper>
    );

    const timeframeSelect = (
        <StyledInlineFilterWrapper>
            <SignalTimeframeSelect
                selectedTimeframe={selectedTimeframe}
                options={appsSignalTimeframeConfig.options}
                onSelect={handleTimeframeSelect}
            />
        </StyledInlineFilterWrapper>
    );

    /**
     * Inline fragments, not blocks: the card is a plain div, so these flow and wrap as one sentence.
     * Renewal reads timeframe-then-filter, mirroring the technology competitor-renewal sentence.
     */
    function renderSentence() {
        const { apps } = signalsI18nKeys.managementSentence;

        if (isRenewal) {
            return (
                <>
                    <StyledDropdownLabelGapWrapped>{translate(apps.renewalPrefix)}</StyledDropdownLabelGapWrapped>
                    <StyledInlineFilterWrapper data-automation="apps-signal-renewal-period-filter">
                        <SignalRenewalPeriodFilter
                            months={renewalMonths}
                            signalAlias={signalAlias}
                            onChange={handleRenewalMonthsChange}
                        />
                    </StyledInlineFilterWrapper>
                    <StyledDropdownLabelGapWrapped>{translate(apps.renewalWith)}</StyledDropdownLabelGapWrapped>
                    {sdksFilter}
                </>
            );
        }

        const leadIn =
            currentFilters.appsSdks?.type === appsSdksSignalChangeTypeMap.removed ? apps.removed : apps.installed;

        return (
            <>
                <StyledDropdownLabelGapWrapped>{translate(leadIn)}</StyledDropdownLabelGapWrapped>
                {sdksFilter}
                <StyledDropdownLabelGapWrapped>
                    {translate(signalsI18nKeys.managementLabel.timeframe)}
                </StyledDropdownLabelGapWrapped>
                {timeframeSelect}
            </>
        );
    }

    return (
        <StyledFiltersViewInner alignItems="center">
            <StyledFiltersViewContent>
                <SignalFiltersViewHeader text={translate(signalsI18nKeys.managementLabel.appSignalType)}>
                    {renderTemplateView({ onSelect: handleTemplateSelect })}
                </SignalFiltersViewHeader>

                {isActive ? <SignalFiltersWrapper>{renderSentence()}</SignalFiltersWrapper> : null}

                {notificationView ? <StyledNotificationWrapper>{notificationView}</StyledNotificationWrapper> : null}
            </StyledFiltersViewContent>

            <StyledModalFooter>
                {renderSubmit({
                    isDisabled: isSubmitDisabled,
                    onSubmit: handleSubmit,
                })}
            </StyledModalFooter>
        </StyledFiltersViewInner>
    );
};

export default AppsSignalFiltersView;
